import { print } from 'graphql';
import { gql } from 'graphql-tag';
import type { RxCollection, RxDocument, RxJsonSchema } from 'rxdb';

import { AdditionalFields } from '../../models/models';
import { getDateModified, getRoleForMutation } from '../../utilities/utilityFunctions';
import { defaultDoc, getValueFromLocalStorage } from '../share/defaultDoc';

export interface InspectionFiles {
  readonly fileId: string;
  readonly name: string;
  readonly fileUUID: string;
  readonly originalFileName: string;
  readonly originalPath: string;
  readonly legacyId: number;
  readonly isImageFile: boolean;
  readonly inspectionId: string;
  readonly showOnReport: boolean;
  readonly fileDescription: string;
  readonly drawingType: number;
  readonly fileCategory: number;
  readonly pcsId: string;
  readonly fkAimsfileTblSp?: string;
  readonly sizeMb: number;
  readonly allowSpaccess: boolean;
  readonly siteId: number;
  readonly disciplineId: number;
  readonly userCreated: string;
  readonly userModified?: string;
  readonly extension: string;
  readonly dateCreated?: string;
  readonly dateModified?: string;
  readonly isDelete?: string;
  readonly fkAssetInspectionTask: number;
  readonly fkInspectorField: number;
  readonly fkInspectorLead: number;
  readonly fileSortOrder?: number;
}

export const inspectionFilesSchema: RxJsonSchema<InspectionFiles> = {
  type: 'object',
  version: 0,
  description: 'describes inspection files',
  primaryKey: 'fileUUID',
  properties: {
    fileUUID: { type: 'string', maxLength: 36 },
    fileId: { type: ['string', 'null'] },
    name: { type: ['string', 'null'] },
    originalFileName: { type: ['string', 'null'] },
    originalPath: { type: ['string', 'null'] },
    legacyId: { type: ['number', 'null'] },
    isImageFile: { type: ['boolean', 'null'] },
    inspectionId: { type: ['string', 'null'] },
    showOnReport: { type: ['boolean', 'null'] },
    fileDescription: { type: ['string', 'null'] },
    drawingType: { type: ['number', 'null'] },
    fileCategory: { type: ['number', 'null'] },
    pcsId: { type: ['string', 'null'] },
    fkAimsfileTblSp: { type: ['string', 'null'] },
    sizeMb: { type: ['number', 'null'] },
    allowSpaccess: { type: ['boolean', 'null'] },
    siteId: { type: ['number', 'null'] },
    disciplineId: { type: ['number', 'null'] },
    userCreated: { type: ['string', 'null'] },
    userModified: { type: ['string', 'null'] },
    extension: { type: ['string', 'null'] },
    dateCreated: { type: ['string', 'null'] },
    dateModified: { type: ['string', 'null'] },
    isDelete: { type: ['string', 'null'] },
    fkAssetInspectionTask: { type: ['number', 'null'] },
    fkInspectorField: { type: ['number', 'null'] },
    fkInspectorLead: { type: ['number', 'null'] },
    fileSortOrder: { type: ['number', 'null'] },
  },
};

export type InspectionFilesCollection = RxCollection<InspectionFiles> | null;
export type InspectionFilesDocument = RxDocument<InspectionFiles>;

export const inspectionFilesPullQueryBuilder = (document: InspectionFiles) => {
  let doc = document;
  if (!doc) {
    (doc as unknown) = { ...defaultDoc };
  }

  const query = print(gql`
    query inspectionFilesFeed(
      $siteId: Int!
      $disciplineId: Int!
      $lastModified: DateTime!
      $isLeadInspector: Boolean!
    ) {
      inspectionFilesFeed(
        siteId: $siteId
        disciplineId: $disciplineId
        lastModified: $lastModified
        isLeadInspector: $isLeadInspector
      ) {
        fileId
        name
        fileUUID
        originalFileName
        originalPath
        legacyId
        isImageFile
        inspectionId
        showOnReport
        fileDescription
        drawingType
        fileCategory
        pcsId
        fkAimsfileTblSp
        sizeMb
        allowSpaccess
        siteId
        disciplineId
        userCreated
        userModified
        extension
        dateCreated
        dateModified
        isDelete
        fkAssetInspectionTask
        fkInspectorField
        fkInspectorLead
        fileSortOrder
      }
    }
  `);
  return {
    query,
    variables: {
      siteId: JSON.parse(getValueFromLocalStorage('selectedSite'))?.selectedSiteId,
      disciplineId: JSON.parse(getValueFromLocalStorage('selectedDiscipline'))
        ?.selectedDisciplineId,
      lastModified: getDateModified(
        doc?.dateModified,
        doc?.dateCreated,
        doc.siteId,
        doc.disciplineId,
        JSON.parse(getValueFromLocalStorage('selectedSite'))?.selectedSiteId,
        JSON.parse(getValueFromLocalStorage('selectedDiscipline'))?.selectedDisciplineId,
      ),
      isLeadInspector: getValueFromLocalStorage('isLeadInspector') === 'True',
    },
  };
};

export const inspectionFilesPushQueryBuilder = (doc: InspectionFiles[]) => {
  const document = doc[0];
  const query = print(gql`
    mutation cEDInspectionFileInfo(
      $role: Int!
      $MobileInspectionFilesFeedInput: MobileInspectionFilesFeedInput
    ) {
      cEDInspectionFileInfo(role: $role, inspectionFileRequest: $MobileInspectionFilesFeedInput) {
        fileId
        uUID
        inspectionId
        result
      }
    }
  `);

  const role = getRoleForMutation(document);

  const MobileInspectionFilesFeedInput = {
    ...document,
    fkInspectorField: document.fkInspectorField || 0,
    fkInspectorLead: document.fkInspectorLead || 0,
  };

  const variables = {
    MobileInspectionFilesFeedInput,
    role,
  };

  return {
    query,
    variables,
  };
};

export const inspectionFilesPushModifier = (doc: InspectionFilesDocument) => {
  const obj: AdditionalFields = {
    ...doc,
  };

  const blackListedProp = ['_meta', 'isDeleted'];
  blackListedProp.forEach(property => {
    obj[property as keyof AdditionalFields] = undefined;
  });

  return obj;
};

export default {};
